import { v4 as uuidv4 } from 'uuid';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { updateCart, setCartLoading } from '../../../../utils/cart/cartUtils';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { dataLayerPushServiceEvent } from '../utils/utils';
import { AddServiceOfferingsInput, BuildInstallSuggestionParams } from './types';
import { ServiceIds } from '../../../../utils/constants/services';
import { OfferingsProps, ServicesProps } from '@jbhi-fi/apis/src/services/types';

const storefrontClient = new ShopifyStorefrontClient();

const oldApplianceRemovalSku = '350099';
const cableHidingSku = '350080';
const INSTALL_TERMS_PATH = '/pages/help-and-support/jb-hi-fi-installation-terms-and-conditions';
const INSTALL_INCLUSIONS_AND_EXCLUSIONS_PATH = '/pages/tv-mounting';

const addServiceOfferings = async (input: AddServiceOfferingsInput) => {
  const { offerings, primaryLineItem, shouldDrawCart = true } = input;

  if (!offerings.length) {
    return undefined;
  }

  try {
    setCartLoading(true);
    const existingServiceLink = primaryLineItem.properties?._serviceLink;
    const hasInstallation = String(offerings.some(o => o.serviceData.typeId === ServiceIds.Install));

    const serviceLinkId = existingServiceLink && existingServiceLink !== '' ? existingServiceLink : uuidv4();

    primaryLineItem.properties = {
      ...primaryLineItem.properties,
      _isAttachedService: 'false',
      _serviceLink: serviceLinkId,
      _legacy_addons_line_key: serviceLinkId,
      _hasInstallation: hasInstallation,
    };

    const primaryLineItemProperties = {
      ...primaryLineItem.properties,
      _isAttachedService: 'false',
      _serviceLink: serviceLinkId,
      _legacy_addons_line_key: serviceLinkId,
      _hasInstallation: hasInstallation,
    };

    await storefrontClient.updateCartLine(
      primaryLineItem.line_id,
      undefined,
      filterAndMapAttributes(primaryLineItemProperties)
    );

    const serviceLines = offerings.map(offering => {
      const { variantId, location, message, serviceData: serviceDataObject } = offering;
      const { requiredServiceData, ...serviceData } = serviceDataObject;

      const serviceProperties = {
        _serviceLink: serviceLinkId,
        _isAttachedService: 'true',
        _serviceData: JSON.stringify(serviceData),
        _serviceSource: location,
        _requiredServiceData: JSON.stringify(requiredServiceData),
        _servicePrice: serviceData.priceInc.toString(),
        _serviceDate: serviceData.extraCareCoveragePeriod,
        _legacy_addons_primary_line_key: serviceLinkId,
        _serviceType: serviceData.typeId,
        _priceRange: JSON.stringify(serviceData.priceRange),
      };

      return {
        line: {
          variantId,
          quantity: 1,
          attributes: filterAndMapAttributes(serviceProperties),
        },
        tracking: {
          location,
          message,
          serviceData,
        },
      };
    });

    const cart = await storefrontClient.addMultipleToCart(serviceLines.map(({ line }) => line));

    if (shouldDrawCart) {
      await updateCart(cart);
    } else {
      setCartLoading(false);
    }

    serviceLines.forEach(({ tracking }) => {
      const { serviceData, location, message } = tracking;
      dataLayerPushServiceEvent(
        `${serviceData.typeId}.addedtocart`,
        primaryLineItem?.onlineProduct?.ProductTags?.GoogleTagManager,
        {
          sku: serviceData.sku,
          name: serviceData.offeringName,
          price: serviceData.priceInc,
        },
        location,
        message
      );
    });

    return cart;
  } catch (e) {
    window.console.error(e);
  }
};

const normaliseSku = (sku?: string | number | null): string | undefined => (sku != null ? String(sku) : undefined);

const parseSkuList = (skusString: string | undefined): Array<string> => {
  if (!skusString) return [];
  return Array.from(
    new Set(
      skusString
        .split(/[,\s]+/)
        .map(s => normaliseSku(s.trim()))
        .filter((s): s is string => !!s)
    )
  );
};

const findProductBySku = (products: Array<AlgoliaProductHit>, sku?: string): AlgoliaProductHit | undefined => {
  const targetSku = normaliseSku(sku);
  if (!targetSku) return undefined;

  return products.find(product => {
    const productSku = normaliseSku(product?.sku);
    if (productSku === targetSku) return true;

    const alternates = (product?.skus ?? []).map(normaliseSku).filter(Boolean) as Array<string>;
    return alternates.includes(targetSku);
  });
};

const isProductAvailable = (product: AlgoliaProductHit): boolean => {
  const { availability } = product;
  if (!availability) return false;

  return availability.displayProduct && availability.canBuyOnline && product.price > 0 && product.product_published;
};

const matchesTvScreenSize = (product: AlgoliaProductHit, targetSizes: Array<number>): boolean => {
  if (!targetSizes.length) return true;

  const facetValues = product.facets?.['Compatible TV size (inches)'];
  if (!Array.isArray(facetValues) || !facetValues.length) return false;

  const productSizes = facetValues.map(value => Number(value)).filter(size => Number.isFinite(size));
  if (!productSizes.length) return false;

  return targetSizes.some(size => productSizes.includes(size));
};

const selectPreferredProducts = (
  products: Array<AlgoliaProductHit>,
  skuList: Array<string>,
  predicate: (product: AlgoliaProductHit) => boolean,
  options: { limit?: number } = {}
): Array<AlgoliaProductHit> => {
  if (!products.length || !skuList.length) return [];

  const selected: Array<AlgoliaProductHit> = [];
  const seen = new Set<string>();

  for (const sku of skuList) {
    if (options.limit && selected.length >= options.limit) break;

    const product = findProductBySku(products, sku);
    if (!product) continue;
    if (seen.has(product.objectID)) continue;
    if (!predicate(product)) continue;

    selected.push(product);
    seen.add(product.objectID);
  }

  return selected;
};

const buildInstallSuggestionResult = ({
  suggestions,
  productSku,
  tvInstallGroups,
  oldApplianceRemovalSku: oldApplianceRemovalSkuOverride = oldApplianceRemovalSku,
  cableHidingSku: cableHidingSkuOverride = cableHidingSku,
}: BuildInstallSuggestionParams) => {
  if (!suggestions?.length) {
    return { currentSuggestion: undefined, removalSuggestion: undefined };
  }

  const suggestion = suggestions.find(li => li.productSku === productSku);
  if (!suggestion) {
    return { currentSuggestion: undefined, removalSuggestion: undefined };
  }

  const installServices = suggestion.services?.filter(service => service.id === ServiceIds.Install) ?? [];
  if (!installServices.length) {
    return { currentSuggestion: undefined, removalSuggestion: undefined };
  }

  let removalSuggestion: ServicesProps | undefined;
  const groups = tvInstallGroups ?? {};

  const enhancedInstallServices = installServices
    .map(service => {
      const offerings = service.offerings ?? [];
      if (!offerings.length) {
        return service;
      }

      const offeringsBySku = new Map<string, OfferingsProps>();
      const enhancedOfferingsBySku = new Map<string, OfferingsProps>();

      offerings.forEach(offering => {
        if (offering.sku === oldApplianceRemovalSkuOverride) {
          removalSuggestion = { ...service, offerings: [offering] };
          return;
        }
        if (offering.sku) {
          offeringsBySku.set(offering.sku, offering);
        }
      });

      Object.values(groups).forEach(group => {
        const groupSkus = group?.skus?.filter(Boolean) ?? [];
        if (!groupSkus.length) return;
        if (groupSkus.length === 1) {
          const sku = groupSkus[0];
          if (!sku) return;
          const baseOffering = offeringsBySku.get(sku);
          if (!baseOffering) return;
          enhancedOfferingsBySku.set(sku, {
            ...baseOffering,
            title: group?.title,
            heading: group?.title,
            description: group?.description,
            subText: group?.description,
            explained: group?.explained,
            skusIncluded: [sku],
            priceToShow: baseOffering.priceInc,
          });
          return;
        }

        const [primarySkuRaw] = groupSkus;
        const primarySku = primarySkuRaw;
        const cableSku = groupSkus.find(sku => sku === cableHidingSkuOverride) || groupSkus[1];
        if (!primarySku || !cableSku) return;

        const primaryOffering = offeringsBySku.get(primarySku);
        const cableOffering = offeringsBySku.get(cableSku);

        if (!primaryOffering || !cableOffering) return;

        enhancedOfferingsBySku.set(cableSku, {
          ...cableOffering,
          title: group?.title ?? cableOffering.title,
          heading: group?.title ?? cableOffering.title,
          description: group?.description ?? cableOffering.description,
          subText: group?.description ?? cableOffering.description,
          explained: group?.explained ?? cableOffering.explained,
          skusIncluded: [primarySku, cableSku],
          priceToShow: primaryOffering.priceInc + cableOffering.priceInc,
        });
      });

      const updatedOfferings = offerings.reduce<Array<OfferingsProps>>((acc, offering) => {
        if (offering.sku === oldApplianceRemovalSkuOverride) return acc;
        const enhanced = offering.sku ? enhancedOfferingsBySku.get(offering.sku) : undefined;
        acc.push(enhanced ?? offering);
        return acc;
      }, []);

      updatedOfferings.sort((a, b) => {
        const priceA = typeof a.priceToShow === 'number' ? a.priceToShow : a.priceInc;
        const priceB = typeof b.priceToShow === 'number' ? b.priceToShow : b.priceInc;
        if (priceA === priceB) return 0;
        return priceB - priceA;
      });

      const enhancedService = { ...service, offerings: updatedOfferings };
      return enhancedService.offerings?.length ? enhancedService : null;
    })
    .filter((svc): svc is ServicesProps => !!svc);

  if (!enhancedInstallServices.length) {
    return { currentSuggestion: undefined, removalSuggestion };
  }

  return {
    currentSuggestion: { ...suggestion, services: enhancedInstallServices },
    removalSuggestion,
  };
};

export {
  addServiceOfferings,
  parseSkuList,
  findProductBySku,
  isProductAvailable,
  matchesTvScreenSize,
  selectPreferredProducts,
  INSTALL_INCLUSIONS_AND_EXCLUSIONS_PATH,
  INSTALL_TERMS_PATH,
  buildInstallSuggestionResult,
  normaliseSku,
  cableHidingSku,
};
